import React, { useCallback, useMemo, useState } from 'react';

import { Linking, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { fromUnixTime } from 'date-fns';

import { BUTTON_SUBSCRIBE, DETAIL_WATCH_BUTTON } from 'config/testID';

import { Bookmark } from 'store/bookmarks/reducer.types';
import { selectLayoutConfig } from 'store/config/selectors';
import { RBProgram } from 'store/railsBuilder/reducer.types';
import { RecordingMetadata } from 'store/recordings/reducer.types';
import { selectIsNonInteractiveUI } from 'store/user/selectors';

import { VodEditorial, isSeriesProgram } from 'services/data-manager/types';

import getCorrectProgramStartAndEndTime from 'utils/getCorrectProgramStartAndEndTime';
import { UseRecordingsHookReturnType } from 'utils/hooks/useRecordings';
import { isTabletOrWeb } from 'utils/responsiveness';

import { isLive } from 'screens/Epg/utils/program';

import ActionButton from 'components/CustomButtons/ActionButton';
import LiveProgressButton from 'components/CustomButtons/LiveProgressButton';
import ProgressButton from 'components/CustomButtons/ProgressButton';
import DialogView from 'components/Dialog';
import dialogStyles from 'components/Dialog/style';

import useModalHandler from '../../hooks/useModalHandler';
import {
    BUTTON_CONTINUE_WATCHING,
    CLOSE_BUTTON,
    HOVER_EPISODE,
    HOVER_SEASON,
    NON_INTERACTIVE_UI_SERVICE_ORDERED_DESCRIPTION_RN,
    NON_INTERACTIVE_UI_SERVICE_ORDERED_TITLE,
    PLAY_DETAIL,
    SCREEN_DETAIL_MANAGE_RECORDING_BUTTON,
    SELFCARE_APP_BUTTON,
    SUBSCRIBE_OVERLAY_SELFCARE_LINK,
} from '../../languageConst';
import { ManageRecordingItem } from '../../types';
import DetailPageDropdownButton from '../DetailPageDropdownButton';
import DetailPageSubscribeButton from '../DetailPageSubscribeButton/DetailPageSubscribeButton';
import DetailPageVodButtons from '../DetailPageVodButtons';
import styles from './styles';

type Props = {
    item: RBProgram | VodEditorial | RecordingMetadata;
    isRecordingDropdownVisible: boolean;
    showRecordingDropdown: () => void;
    hideRecordingDropdown: () => void;
    ableToPlayLive: boolean;
    ableToPlayCatchup: boolean;
    isChannelSubscribed: boolean;
    ableToPlayVod: boolean;
    primaryColor?: string;
    onWatchPress(): void;
    onManageRecordingPress?: (item: ManageRecordingItem) => void;
    isVod?: boolean;
    bookmark: Bookmark | undefined;
    videoDuration: number;
    canCloseButtonDropdown?: boolean;
    canWatchRecording: UseRecordingsHookReturnType['canWatchRecording'];
    recordingsButtonsList: UseRecordingsHookReturnType['recordingsButtonsList'];
    hasGlobalRecordingPermission: UseRecordingsHookReturnType['hasGlobalRecordingPermission'];
    kidsProfileActive: boolean;
};

function DetailPageButtons({
    item,
    isRecordingDropdownVisible,
    showRecordingDropdown,
    hideRecordingDropdown,
    primaryColor,
    ableToPlayLive,
    ableToPlayCatchup,
    isChannelSubscribed,
    ableToPlayVod,
    onWatchPress,
    onManageRecordingPress,
    isVod = false,
    bookmark,
    videoDuration,
    canCloseButtonDropdown,
    canWatchRecording,
    recordingsButtonsList,
    hasGlobalRecordingPermission,
    kidsProfileActive,
}: Props) {
    const { t } = useTranslation();
    const { primary } = useSelector(selectLayoutConfig);

    const isNonInteractive = useSelector(selectIsNonInteractiveUI);

    const [isSelfcareModalOpen, setIsSelfcareModalOpen] = useState(false);

    const { SeasonNumber, episodeNumber } = item;
    const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] =
        useState(false);

    const { onOpenModal, onCloseModal } = useModalHandler({
        setModalOpen: setIsSubscriptionModalOpen,
    });

    const { programStartTime, programEndTime } =
        getCorrectProgramStartAndEndTime(item);

    const ifSeasonOrEpisode = SeasonNumber != null || episodeNumber != null;
    const isAssetLive = isLive(item);
    const isAssetPlayable =
        (ableToPlayLive || ableToPlayCatchup || canWatchRecording) &&
        isChannelSubscribed;

    const watchButtonLabel = useMemo(() => {
        if (bookmark?.position) {
            return t(BUTTON_CONTINUE_WATCHING);
        }

        const title = [t(PLAY_DETAIL)];

        if (isSeriesProgram(item) && SeasonNumber != null) {
            title.push(`${t(HOVER_SEASON)}${SeasonNumber}`);
        }

        if (isSeriesProgram(item) && episodeNumber != null) {
            title.push(`${t(HOVER_EPISODE)}${episodeNumber}`);
        }

        return title.join(' ');
    }, [t, item, SeasonNumber, episodeNumber, bookmark?.position]);

    const handleSubscriptionRedirect = useCallback(() => {
        Linking.openURL(t(SUBSCRIBE_OVERLAY_SELFCARE_LINK));
    }, []);

    const onOpenSelfcareModal = useCallback(() => {
        setIsSelfcareModalOpen(true);
    }, []);

    const onCloseSelfcareModal = useCallback(() => {
        setIsSelfcareModalOpen(false);
    }, []);

    const renderVod = useCallback(() => {
        if (isVod && !isNonInteractive) {
            return (
                <DetailPageVodButtons
                    item={item as VodEditorial}
                    ableToPlayVod={ableToPlayVod}
                    primaryColor={primaryColor}
                    watchButtonLabel={watchButtonLabel}
                    bookmark={bookmark}
                    videoDuration={videoDuration}
                    kidsProfileActive={kidsProfileActive}
                    onWatchPress={onWatchPress}
                />
            );
        }

        return null;
    }, [
        ableToPlayVod,
        isVod,
        item,
        onWatchPress,
        primaryColor,
        isNonInteractive,
        watchButtonLabel,
        kidsProfileActive,
        bookmark,
        videoDuration,
    ]);

    const renderWatchButton = useCallback(() => {
        if (!isAssetPlayable && isNonInteractive) {
            return (
                <>
                    {isSelfcareModalOpen && (
                        <DialogView
                            style={dialogStyles.nonTransparentDialog}
                            title={NON_INTERACTIVE_UI_SERVICE_ORDERED_TITLE}
                            description={
                                NON_INTERACTIVE_UI_SERVICE_ORDERED_DESCRIPTION_RN
                            }
                            visible={isSelfcareModalOpen}
                            actionButtonTitle={SELFCARE_APP_BUTTON}
                            actionButtonFillColor={primary}
                            actionButtonTextStyle={styles.buttonText}
                            actionButtonOnPress={handleSubscriptionRedirect}
                            actionButtonStyle={
                                isTabletOrWeb && styles.actionButtonStyle
                            }
                            cancelButtonTitle={CLOSE_BUTTON}
                            cancelButtonBordered
                            cancelButtonOnPress={onCloseSelfcareModal}
                            actionButtonTestID={BUTTON_SUBSCRIBE}
                            closeButtonTestID={CLOSE_BUTTON}
                            descriptionNumberOfLines={0}
                        />
                    )}
                    <ActionButton
                        numberOfLines={1}
                        buttonStyle={[
                            styles.button,
                            styles.watchButton,
                            isTabletOrWeb && styles.buttonNonScaling,
                        ]}
                        buttonTextStyle={styles.buttonText}
                        title={SELFCARE_APP_BUTTON}
                        fillColor={primary}
                        onPress={onOpenSelfcareModal}
                    />
                </>
            );
        }

        if (!isChannelSubscribed && !isVod) {
            return (
                <DetailPageSubscribeButton
                    isModalOpen={isSubscriptionModalOpen}
                    onCloseModal={onCloseModal}
                    onOpenModal={onOpenModal}
                    ableToPlayVod={false}
                />
            );
        }

        if (isAssetPlayable) {
            return (
                <View>
                    {(isAssetLive && programStartTime && programEndTime && (
                        <LiveProgressButton
                            style={[
                                styles.button,
                                styles.watchButton,
                                ifSeasonOrEpisode && styles.watchSeriesButton,
                            ]}
                            startTime={fromUnixTime(programStartTime)}
                            endTime={fromUnixTime(programEndTime)}
                            isLive={isAssetLive}
                            buttonMinWidth={
                                ifSeasonOrEpisode
                                    ? styles.watchSeriesButton.minWidth
                                    : styles.watchButton.minWidth
                            }
                            label={watchButtonLabel}
                            onPress={onWatchPress}
                            testID={DETAIL_WATCH_BUTTON}
                        />
                    )) || (
                        <ProgressButton
                            style={[
                                styles.button,
                                styles.watchButton,
                                ifSeasonOrEpisode && styles.watchSeriesButton,
                            ]}
                            progress={bookmark?.position}
                            duration={videoDuration}
                            buttonMinWidth={
                                ifSeasonOrEpisode
                                    ? styles.watchSeriesButton.minWidth
                                    : styles.watchButton.minWidth
                            }
                            label={watchButtonLabel}
                            onPress={onWatchPress}
                            testID={DETAIL_WATCH_BUTTON}
                        />
                    )}
                </View>
            );
        }

        return null;
    }, [
        isAssetLive,
        programStartTime,
        programEndTime,
        canWatchRecording,
        onWatchPress,
        watchButtonLabel,
        ifSeasonOrEpisode,
        isAssetPlayable,
        isNonInteractive,
        onOpenSelfcareModal,
        onCloseSelfcareModal,
        handleSubscriptionRedirect,
        primary,
        isSelfcareModalOpen,
        isVod,
        isSubscriptionModalOpen,
        bookmark?.position,
        videoDuration,
        onCloseModal,
        onOpenModal,
    ]);

    const renderRecordButton = useCallback(() => {
        if (
            !hasGlobalRecordingPermission ||
            kidsProfileActive ||
            !isChannelSubscribed
        ) {
            return null;
        }

        const isSingleButton =
            !(ableToPlayLive || ableToPlayCatchup || canWatchRecording) &&
            !isVod;

        return recordingsButtonsList.length === 1 ? (
            <ActionButton
                numberOfLines={1}
                buttonStyle={[
                    styles.button,
                    styles.watchButton,
                    styles.recordButton,
                    isSingleButton && styles.singleRecordButton,
                ]}
                title={recordingsButtonsList[0].label}
                onPress={() =>
                    onManageRecordingPress?.(recordingsButtonsList[0])
                }
                buttonTextStyle={
                    isSingleButton ? styles.buttonText : styles.recordButtonText
                }
                testID={recordingsButtonsList[0].testID}
            />
        ) : (
            <DetailPageDropdownButton
                title={SCREEN_DETAIL_MANAGE_RECORDING_BUTTON}
                isRecordingDropdownVisible={isRecordingDropdownVisible}
                showRecordingDropdown={showRecordingDropdown}
                hideRecordingDropdown={hideRecordingDropdown}
                onSelectItem={(selectItem) =>
                    onManageRecordingPress?.(selectItem as ManageRecordingItem)
                }
                recordingOptions={recordingsButtonsList}
                buttonStyle={[
                    styles.recordButton,
                    styles.watchButton,
                    isSingleButton && styles.singleRecordButton,
                ]}
                buttonTextStyle={
                    isSingleButton ? styles.buttonText : styles.recordButtonText
                }
                useWhiteArrowIcon={!isSingleButton}
                canCloseButtonDropdown={canCloseButtonDropdown}
            />
        );
    }, [
        isChannelSubscribed,
        hasGlobalRecordingPermission,
        ableToPlayLive,
        ableToPlayCatchup,
        canWatchRecording,
        isVod,
        recordingsButtonsList,
        canCloseButtonDropdown,
        isRecordingDropdownVisible,
        kidsProfileActive,
        onManageRecordingPress,
        showRecordingDropdown,
        hideRecordingDropdown,
    ]);

    return (
        <View style={styles.root}>
            {renderVod()}
            {renderWatchButton()}
            {renderRecordButton()}
        </View>
    );
}

export default DetailPageButtons;
